import moment from 'moment-timezone';

import { pad, getMinimumAge } from './miscHelper';

export const isValid = (date) => !!(date && moment(date).isValid());

export const isDayMonthYearADate = (day, month, year) => {
  // Used to validate b-days
  if (!day || !month || !year) {
    return false;
  }
  const dateString = convertDayMonthYearToDate(day, month, year);

  return moment(dateString, 'YYYY-MM-DD', true).isValid();
};

export const convertISODateTimeStringToISODate = (date, time, settings) => {
  if (!date) {
    throw new Error('Invalid Date');
  }
  const dateTime = `${date} ${time}`;
  if (moment(dateTime, 'YYYY-MM-DD HH:mm:ss', true).isValid())
    return moment
      .tz(dateTime, 'YYYY-MM-DD HH:mm:ss', timeZone(settings))
      .toISOString();

  throw new Error('Invalid Date');
};

export const convertUTCDateTimeStringToISODate = (date, time) => {
  const dateTime = `${date} ${time}`;
  if (moment(dateTime, 'MMM D, YYYY h:mm a', true).isValid())
    return moment.tz(dateTime, 'MMM D, YYYY h:mm a', 'UTC').toISOString();
  return null;
};

export const convertDateTimeStringToISODate = (date, time, settings) => {
  const dateTime = `${date} ${time}`;
  if (moment(dateTime, 'MMM D, YYYY h:mm a', true).isValid())
    return moment
      .tz(dateTime, 'MMM D, YYYY h:mm a', timeZone(settings))
      .toISOString();
  return null;
};

export const convertDateStringToMomentDate = (date) => {
  if (moment(date, 'MMM D, YYYY', true).isValid())
    return moment(date, 'MMM D, YYYY');
  return null;
};

export const convertDateStringToISODate = (date) => {
  if (moment(date, 'MMM D, YYYY', true).isValid())
    return moment(date, 'MMM D, YYYY').format('YYYY-MM-DD');
  throw new Error('Invalid Date');
};

export const convertShipCompliantTimeTOISODate = (time, settings) => {
  const dateTime = `2018-01-01 ${time}`;
  if (moment(dateTime, 'YYYY-MM-DD HH:mm:ss', true).isValid())
    return moment
      .tz(dateTime, 'YYYY-MM-DD HH:mm:ss', timeZone(settings))
      .toISOString();
  return null;
};

export const convertDayMonthYearToDate = (day, month, year) => {
  // Used to convert b-days
  const dateString = `${pad('0000', year)}-${pad('00', month)}-${pad(
    '00',
    day
  )}`;
  return dateString;
};

export const dateAddDay = (date, days, settings) => {
  if (!isValid(date)) {
    throw new Error('Invalid Date');
  }
  return moment(date)
    .tz(timeZone(settings))
    .add(days, 'days')
    .format('YYYY-MM-DD');
};

export const dateFormatNoTimeZone = (date, format) => {
  if (!isValid(date)) {
    throw new Error('Invalid Date');
  }
  switch (format) {
    case 'isoDate':
      return moment(date).format('YYYY-MM-DD');
    case 'darioush':
      return moment(date).format('MMM YYYY');
    case 'darioushLong':
      return moment(date).format('MMMM YYYY');
    default:
      return moment(date).format('MMM D, YYYY');
  }
};

export const dateFormat = (date, settings, format) => {
  if (!isValid(date)) {
    throw new Error('Invalid Date');
  }
  switch (format) {
    case 'isoDate':
      return moment(date).tz(timeZone(settings)).format('YYYY-MM-DD');
    default:
      return moment(date).tz(timeZone(settings)).format('MMM D, YYYY');
  }
};

export const timeFormat = (date, settings, format) => {
  if (!date || !moment(date).isValid()) {
    throw new Error('Invalid Date');
  }
  switch (format) {
    case 'isoDate':
      return moment(date).tz(timeZone(settings)).format('HH:mm:SS');
    default:
      return moment(date).tz(timeZone(settings)).format('h:mm a');
  }
};

export const getClientTimezone = () => {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  return timeZone;
};

export const timeFormatNoSettings = (date, timeZone, format) => {
  if (!date || !moment(date).isValid()) {
    throw new Error('Invalid Date');
  }
  switch (format) {
    case 'isoDate':
      return moment(date).tz(timeZone).format('HH:mm:SS');
    default:
      return moment(date).tz(timeZone).format('h:mm a');
  }
};

export const timeZone = (settings) => settings.data.timeZone;

export const validateMinimumAge = (
  birthDay,
  birthMonth,
  birthYear,
  settings
) => {
  const minimumAge = getMinimumAge(settings);
  const birthDate = new Date();
  birthDate.setFullYear(birthYear, birthMonth - 1, birthDay);

  const ageCheck = new Date();

  // this is the min age the customer must be
  ageCheck.setFullYear(ageCheck.getFullYear() - minimumAge);

  if (ageCheck - birthDate < 0) {
    return false;
  }

  return true;
};

export const isValidBirthDate = (birthDay, birthMonth, birthYear) => {
  const birthDate = new Date();
  birthDate.setFullYear(birthYear, birthMonth - 1, birthDay);
  const yearCheck = birthDate.getFullYear();
  if (yearCheck < 1900) {
    return false;
  }
  return true;
};

export const timeOnlyFormat = (time) => {
  if (!time) {
    throw new Error('Invalid Date');
  }
  if (moment(time, 'HH:mm:ss', true).isValid())
    return moment(time, 'HH:mm:ss').format('h:mm a');

  throw new Error('Invalid Date');
};

export const dayOfWeekAsString = (day) =>
  [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ][day];
